/**
 * Client-side plant name pun generator.
 * No API calls — instant results, zero cost.
 */

interface PlantNameInput {
  commonName: string;
  scientificName?: string;
}

/** Known puns for specific common plants */
const KNOWN_PUNS: Record<string, string[]> = {
  'fern': ['Fern-ando', 'Fernie Sanders', 'Fern Gully', 'Fernie Mac'],
  'monstera': ['Monstera Mia', 'Frank-en-plant', 'Mon-star', 'Monstera Lisa'],
  'pothos': ['Pothos Malone', 'Sir Pothos-a-lot', 'Pothos Everdeen'],
  'snake plant': ['Snakey McSnakeface', 'Hiss-teria', 'Sir Slithers', 'Slinky'],
  'succulent': ['Sir Succs-a-lot', 'Succa Free', 'Thicc-ulent'],
  'cactus': ['Spike', 'Prickles', 'Señor Pointy', 'Cact-Jack'],
  'aloe': ['Aloe There', 'Aloe Vera Wang', 'Aloe-ha'],
  'fiddle leaf fig': ['Fiddle-sticks', 'Fig Newton', 'The Diva', 'Sir Fiddles'],
  'peace lily': ['Lily Allen', 'Lily of the Valley Girl', 'Peaceful Pete'],
  'rubber plant': ['Stretch', 'Bouncy', 'Gumby', 'Rubber Ducky'],
  'spider plant': ['Spidey', 'Charlotte', 'Web Slinger', 'Peter Parker'],
  'philodendron': ['Phil', 'Phil Collins', 'Phil-thy Rich'],
  'jade': ['Jade Pinkett', 'Jade-n Smith', 'Lil Jade'],
  'orchid': ['Orchid-elia', 'Your Orchid-ness', 'Bloom Boom'],
  'basil': ['Basil Fawlty', 'Basil Brush', 'Sweet Baz'],
  'mint': ['Minty Fresh', 'Pepper Minty', 'Slim Minty'],
  'lavender': ['Lady Lav', 'Lavender Brown', 'Lav-ley'],
  'ivy': ['Poison Ivy', 'Ivy League', 'Miss Ivy'],
  'palm': ['Palmy Days', 'Palm Pilot', 'Palmela Anderson'],
  'bamboo': ['Bam-Bam', 'Bamboo-zle', 'Sir Bamboo'],
  'calathea': ['Cal', 'Drama Queen', 'Callie'],
  'begonia': ['Bego-nya Business', 'Begon Thot'],
  'zz plant': ['ZZ Top', 'Snoozy', 'Easy ZZ'],
  'string of pearls': ['Pearl Jam', 'Pearly Gates', 'Cleopatra'],
  'string of hearts': ['Heartbreaker', 'Lovey', 'String Bean'],
  'boston fern': ['Fernie Sanders', 'The Bostonian', 'Fern-ando'],
  'bird of paradise': ['Birdy', 'Paradise Pete', 'Tropicana'],
  'chinese money plant': ['Mr. Moneybags', 'Penny', 'Cash Money'],
  'rosemary': ['Rosie', 'Mary Rose', 'Rosemary Clooney'],
  'dracaena': ['Drake', 'Drac', 'Dragon Lady'],
};

/** Template patterns for generating puns from any plant name */
const TEMPLATES = [
  (name: string) => `Sir ${name}`,
  (name: string) => `Little ${name}`,
  (name: string) => `${name} Jr.`,
  (name: string) => `Baby ${name}`,
  (name: string) => `Professor ${name}`,
  (name: string) => `Captain ${name}`,
  (name: string) => `Lord ${name}`,
  (name: string) => `${name} the Magnificent`,
  (name: string) => `${name} the Great`,
  (name: string) => `Big ${name}`,
  (name: string) => `Lil ${name}`,
  (name: string) => `Señor ${name}`,
  (name: string) => `Lady ${name}`,
  (name: string) => `${name}-kins`,
  (name: string) => `${name}-ster`,
];

/** General plant/nature-themed punny names (species-agnostic) */
const GENERIC_PUNS = [
  'Groot',
  'Planty McPlantface',
  'Chloro-Phil',
  'Leaf Erikson',
  'Plant Daddy',
  'Audrey II',
  'The Green Goblin',
  'Photosyn-thesis',
  'Morgan Tree-man',
  'Elvis Parsley',
  'Treebeard',
  'Sprout',
  'Bud',
  'Woody',
  'Ferndinand',
  'Chris-anthemum Pratt',
  'Herb',
  'Thyme Turner',
  'Al-leaf-ander',
];

function shuffle<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = copy[i] as T;
    copy[i] = copy[j] as T;
    copy[j] = temp;
  }
  return copy;
}

/**
 * Generate punny plant name suggestions.
 * Strategy: known puns first, then template substitution, then generic puns.
 */
export function generatePlantNames(
  { commonName, scientificName }: PlantNameInput,
  count = 5
): string[] {
  const results: string[] = [];
  const seen = new Set<string>();

  const addUnique = (name: string) => {
    const key = name.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      results.push(name);
    }
  };

  // 1. Check known puns (match on lowercase common name or first word)
  const nameLower = commonName.toLowerCase();
  const firstWord = nameLower.split(' ')[0] ?? commonName;

  for (const [key, puns] of Object.entries(KNOWN_PUNS)) {
    if (nameLower.includes(key) || key.includes(firstWord)) {
      shuffle(puns).forEach(addUnique);
    }
  }

  // 2. Template substitution using first word of common name
  const shortName = commonName.split(' ')[0] ?? commonName;
  shuffle(TEMPLATES).forEach((template) => {
    if (results.length < count * 2) {
      addUnique(template(shortName));
    }
  });

  // 3. If scientific name available, add a couple from that
  if (scientificName) {
    const genus = scientificName.split(' ')[0] ?? '';
    if (genus.length > 3) {
      addUnique(`${genus}-ino`);
      addUnique(`Dr. ${genus}`);
    }
  }

  // 4. Fill remaining with generic puns
  shuffle(GENERIC_PUNS).forEach((pun) => {
    if (results.length < count * 2) {
      addUnique(pun);
    }
  });

  // Return shuffled subset
  return shuffle(results).slice(0, count);
}
